import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge, Button, EmptyState, Field, Section, Select, Spinner } from '@zt/ui';
import type { Tone } from '@zt/ui';
import { LocalePicker, localeName, useContentLocale } from '../components/ContentLocale';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { usePermissions } from '../lib/session';
import type { ContentCalendar, ContentCalendarItem, ContentReport } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';

/**
 * İçerik takvimi ve içerik raporu (F5B-19, ADR-076). Takvim ayın yayın olaylarını gün gün listeler (Türkiye
 * saati); rapor durum sayıları, kategori dağılımı, aylık yayın, güncellenmeyen yazılar ve Search Console
 * tıklamalarıdır.
 */

const TZ = 'Europe/Istanbul';
const thisMonth = () => new Intl.DateTimeFormat('sv-SE', { timeZone: TZ }).format(new Date()).slice(0, 7);
const shiftMonth = (ym: string, n: number) => {
  const d = new Date(Date.UTC(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

function useIntlTag() {
  const { locale } = useI18n();
  return locale === 'tr' ? 'tr-TR' : locale === 'en' ? 'en-GB' : locale;
}

const STATE_TONE: Record<string, Tone> = {
  draft: 'neutral',
  scheduled: 'amber',
  live: 'moss',
  expired: 'slate',
  unpublished: 'slate',
  inactive: 'slate',
};
const STATE_NS = {
  post: 'admin.blog.states',
  page: 'admin.pages.states',
  banner: 'admin.banners.states',
} as const;
type Kind = ContentCalendarItem['kind'];
const KINDS: Kind[] = ['post', 'page', 'banner'];

function ItemLink({ item }: { item: ContentCalendarItem }) {
  const cls = 'font-medium text-pine hover:underline';
  if (item.kind === 'post')
    return (
      <Link to="/blog/$id" params={{ id: item.id }} className={cls}>
        {item.title}
      </Link>
    );
  if (item.kind === 'page')
    return (
      <Link to="/sayfalar/$id" params={{ id: item.id }} className={cls}>
        {item.title}
      </Link>
    );
  return (
    <Link to="/bannerlar/$id" params={{ id: item.id }} className={cls}>
      {item.title}
    </Link>
  );
}

// ---------------------------------------------------------------------------- Takvim

/** İçerik takvimi: ayın planlı ve gerçekleşen yayın olayları, gün gün. */
export function ContentCalendarPage() {
  const t = useT();
  const tag = useIntlTag();
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const { locales } = useContentLocale(storeId);
  const [month, setMonth] = useState(thisMonth);
  const [kind, setKind] = useState<'all' | Kind>('all');
  const cal = useQuery({
    queryKey: ['content-calendar', storeId, month],
    queryFn: () => api.get<ContentCalendar>(`/v1/admin/content/calendar?storeId=${storeId}&month=${month}`),
    enabled: !!storeId,
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  const monthTitle = new Intl.DateTimeFormat(tag, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${month}-01T00:00:00Z`),
  );
  const items = (cal.data?.items ?? []).filter((i) => kind === 'all' || i.kind === kind);
  const days = [...new Set(items.map((i) => i.day))];
  const planned = items.filter((i) => i.planned).length;
  return (
    <PageFrame helpKey="page.content_calendar">
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-4">
            <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
            <Field
              label={t('admin.contentPlan.kind')}
              help={<HelpTip k="content_calendar.kind" />}
              className="w-56"
            >
              {(p) => (
                <Select {...p} value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
                  <option value="all">{t('admin.contentPlan.allKinds')}</option>
                  {KINDS.map((k) => (
                    <option key={k} value={k}>
                      {t(`admin.contentPlan.kinds.${k}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <Section
            title={monthTitle}
            description={
              cal.data ? t('admin.contentPlan.summary', { planned, done: items.length - planned }) : undefined
            }
            action={
              <div
                className="flex flex-wrap items-center gap-2"
                role="group"
                aria-label={t('admin.contentPlan.monthNav')}
              >
                <Button size="sm" onClick={() => setMonth((m) => shiftMonth(m, -1))}>
                  <ChevronLeft className="h-4 w-4" aria-hidden />
                  {t('admin.contentPlan.prevMonth')}
                </Button>
                <Button size="sm" onClick={() => setMonth(thisMonth())} disabled={month === thisMonth()}>
                  {t('admin.contentPlan.thisMonth')}
                </Button>
                <Button size="sm" onClick={() => setMonth((m) => shiftMonth(m, 1))}>
                  {t('admin.contentPlan.nextMonth')}
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </Button>
                <HelpTip k="content_calendar.month" />
              </div>
            }
            className="mt-6"
          >
            {!cal.data ? (
              <Spinner label={t('common.loading')} />
            ) : !items.length ? (
              <EmptyState
                title={t('admin.contentPlan.empty')}
                description={t('admin.contentPlan.emptyLead')}
              />
            ) : (
              <ol className="space-y-5" aria-label={t('admin.contentPlan.agenda', { month: monthTitle })}>
                {days.map((day) => (
                  <li key={day}>
                    <h3 className="mb-2 flex items-center gap-2 font-medium text-ink">
                      {new Intl.DateTimeFormat(tag, {
                        day: 'numeric',
                        month: 'long',
                        weekday: 'long',
                        timeZone: 'UTC',
                      }).format(new Date(`${day}T00:00:00Z`))}
                      {day === cal.data.today && <Badge tone="pine">{t('admin.contentPlan.today')}</Badge>}
                    </h3>
                    <ul className="divide-y divide-line rounded-md border border-line bg-surface">
                      {items
                        .filter((i) => i.day === day)
                        .map((i) => (
                          <CalendarRow
                            key={`${i.kind}-${i.id}-${i.event}-${i.at}`}
                            item={i}
                            showLocale={locales.length > 1}
                          />
                        ))}
                    </ul>
                  </li>
                ))}
              </ol>
            )}
            {cal.data?.truncated && (
              <p className="mt-3 text-sm text-ink-3">{t('admin.contentPlan.truncated')}</p>
            )}
          </Section>
        </>
      )}
    </PageFrame>
  );
}

function CalendarRow({ item: i, showLocale }: { item: ContentCalendarItem; showLocale: boolean }) {
  const t = useT();
  const tag = useIntlTag();
  const time = new Intl.DateTimeFormat(tag, { hour: '2-digit', minute: '2-digit', timeZone: TZ }).format(
    new Date(i.at),
  );
  const event =
    i.event === 'update'
      ? t('admin.contentPlan.events.update', { version: i.version ?? '' })
      : t(`admin.contentPlan.events.${i.event}_${i.planned ? 'planned' : 'done'}`);
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3 text-sm">
      <time dateTime={i.at} className="w-12 shrink-0 tabular-nums text-ink-2">
        {time}
      </time>
      <div className="min-w-0 flex-1">
        <ItemLink item={i} />
        <p className="text-ink-3">
          {t(`admin.contentPlan.kinds.${i.kind}`)} · {event}
          {showLocale ? ` · ${localeName(i.locale)}` : ''}
        </p>
      </div>
      {i.planned && <Badge tone="amber">{t('admin.contentPlan.planned')}</Badge>}
      <Badge tone={STATE_TONE[i.state] ?? 'neutral'}>{t(`${STATE_NS[i.kind]}.${i.state}`)}</Badge>
    </li>
  );
}

// ---------------------------------------------------------------------------- Rapor

/** İçerik raporu: durumlar, kategoriler, aylık yayın, güncellenmeyen yazılar, Search Console. */
export function ContentReportPage() {
  const t = useT();
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const { locales, locale, setLocale } = useContentLocale(storeId);
  const report = useQuery({
    queryKey: ['content-report', storeId, locale],
    queryFn: () => api.get<ContentReport>(`/v1/admin/content/report?storeId=${storeId}&locale=${locale}`),
    enabled: !!storeId && !!locale,
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  const r = report.data;
  return (
    <PageFrame helpKey="page.content_report">
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-4">
            <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
            <LocalePicker locales={locales} locale={locale} onChange={setLocale} />
          </div>
          {!r ? (
            <Spinner label={t('common.loading')} />
          ) : (
            <>
              <StatesSection r={r} />
              <CategoriesSection r={r} />
              <MonthlySection r={r} />
              <StaleSection r={r} />
              <SearchConsoleSection r={r} defaultLocale={locales[0] === r.locale} />
            </>
          )}
        </>
      )}
    </PageFrame>
  );
}

function StatesSection({ r }: { r: ContentReport }) {
  const t = useT();
  const { formatNumber } = useI18n();
  const groups: Array<{ key: string; title: string; ns: string; rows: Array<[string, number]> }> = [
    {
      key: 'posts',
      title: t('admin.contentPlan.postsTitle'),
      ns: STATE_NS.post,
      rows: [
        ['live', r.posts.live],
        ['scheduled', r.posts.scheduled],
        ['draft', r.posts.draft],
        ['unpublished', r.posts.unpublished],
      ],
    },
    {
      key: 'pages',
      title: t('admin.contentPlan.pagesTitle'),
      ns: STATE_NS.page,
      rows: [
        ['live', r.pages.live],
        ['scheduled', r.pages.scheduled],
        ['draft', r.pages.draft],
        ['expired', r.pages.expired],
        ['unpublished', r.pages.unpublished],
      ],
    },
    {
      key: 'banners',
      title: t('admin.contentPlan.bannersTitle'),
      ns: STATE_NS.banner,
      rows: [
        ['live', r.banners.live],
        ['scheduled', r.banners.scheduled],
        ['draft', r.banners.draft],
        ['expired', r.banners.expired],
        ['inactive', r.banners.inactive],
      ],
    },
  ];
  return (
    <Section
      title={t('admin.contentPlan.statesTitle')}
      action={<HelpTip k="content_report.states" />}
      className="mt-6"
    >
      <div className="grid gap-4 sm:grid-cols-3">
        {groups.map((g) => (
          <div key={g.key} className="rounded-md border border-line px-4 py-3">
            <h3 className="font-medium text-ink">{g.title}</h3>
            <dl className="mt-2 space-y-1 text-sm">
              {g.rows.map(([state, n]) => (
                <div key={state} className="flex justify-between gap-3">
                  <dt className="text-ink-2">{t(`${g.ns}.${state}`)}</dt>
                  <dd className="tabular-nums font-medium">{formatNumber(n)}</dd>
                </div>
              ))}
              {g.key === 'posts' && (
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-2">{t('admin.contentPlan.pendingReview')}</dt>
                  <dd className="tabular-nums font-medium">{formatNumber(r.posts.pendingReview)}</dd>
                </div>
              )}
            </dl>
          </div>
        ))}
      </div>
    </Section>
  );
}

function CategoriesSection({ r }: { r: ContentReport }) {
  const t = useT();
  const { formatNumber } = useI18n();
  return (
    <Section
      title={t('admin.contentPlan.categoriesTitle')}
      description={t('admin.contentPlan.categoriesLead')}
    >
      {!r.categories.length ? (
        <p className="text-sm text-ink-2">{t('admin.contentPlan.noCategories')}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th scope="col" className="px-3 py-2 text-start font-medium">
                  {t('admin.blog.category')}
                </th>
                <th scope="col" className="px-3 py-2 text-end font-medium">
                  {t('admin.contentPlan.livePosts')}
                </th>
              </tr>
            </thead>
            <tbody>
              {r.categories.map((c) => (
                <tr key={c.id ?? 'none'} className="border-t border-line">
                  <td className="px-3 py-2">{c.name ?? t('admin.blog.noCategory')}</td>
                  <td className="px-3 py-2 text-end tabular-nums">{formatNumber(c.live)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function MonthlySection({ r }: { r: ContentReport }) {
  const t = useT();
  const tag = useIntlTag();
  const { formatNumber } = useI18n();
  const max = Math.max(1, ...r.monthly.map((m) => m.count));
  const label = (ym: string) =>
    new Intl.DateTimeFormat(tag, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
      new Date(`${ym}-01T00:00:00Z`),
    );
  return (
    <Section title={t('admin.contentPlan.monthlyTitle')} description={t('admin.contentPlan.monthlyLead')}>
      <div className="overflow-x-auto rounded-md border border-line">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-ink-2">
            <tr>
              <th scope="col" className="px-3 py-2 text-start font-medium">
                {t('admin.contentPlan.month')}
              </th>
              <th scope="col" className="px-3 py-2 text-end font-medium">
                {t('admin.contentPlan.published')}
              </th>
              <th scope="col" className="w-1/2 px-3 py-2">
                <span className="sr-only">{t('admin.contentPlan.bar')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {[...r.monthly].reverse().map((m) => (
              <tr key={m.month} className="border-t border-line">
                <td className="px-3 py-1.5">{label(m.month)}</td>
                <td className="px-3 py-1.5 text-end tabular-nums">{formatNumber(m.count)}</td>
                <td className="px-3 py-1.5" aria-hidden>
                  <div className="h-2 rounded-full bg-pine" style={{ width: `${(m.count / max) * 100}%` }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function StaleSection({ r }: { r: ContentReport }) {
  const t = useT();
  const { formatDate } = useI18n();
  return (
    <Section
      title={t('admin.contentPlan.staleTitle')}
      description={t('admin.contentPlan.staleLead', { days: r.stale.days })}
      action={<HelpTip k="content_report.stale" />}
    >
      {!r.stale.items.length ? (
        <p className="text-sm text-ink-2">{t('admin.contentPlan.staleNone')}</p>
      ) : (
        <>
          <ul className="divide-y divide-line rounded-md border border-line bg-surface">
            {r.stale.items.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm">
                <Link to="/blog/$id" params={{ id: p.id }} className="font-medium text-pine hover:underline">
                  {p.title}
                </Link>
                <span className="text-ink-3">
                  {t('admin.contentPlan.lastPublished', { date: formatDate(p.lastPublishedAt, false) })}
                </span>
              </li>
            ))}
          </ul>
          {r.stale.total > r.stale.items.length && (
            <p className="mt-2 text-sm text-ink-3">
              {t('admin.contentPlan.staleMore', { count: r.stale.total - r.stale.items.length })}
            </p>
          )}
        </>
      )}
    </Section>
  );
}

function SearchConsoleSection({ r, defaultLocale }: { r: ContentReport; defaultLocale: boolean }) {
  const t = useT();
  const { can } = usePermissions();
  const { formatDate, formatNumber, locale } = useI18n();
  if (!can('seo.read')) return null;
  const sc = r.searchConsole;
  const tag = locale === 'tr' ? 'tr-TR' : 'en-GB';
  const pct = new Intl.NumberFormat(tag, { style: 'percent', maximumFractionDigits: 1 });
  const dec = new Intl.NumberFormat(tag, { maximumFractionDigits: 1, minimumFractionDigits: 1 });
  return (
    <Section title={t('admin.contentPlan.scTitle')} action={<HelpTip k="content_report.search_console" />}>
      {!sc ? (
        <p className="text-sm text-ink-2">
          {defaultLocale ? t('admin.contentPlan.scUnavailable') : t('admin.contentPlan.scOtherLocale')}
        </p>
      ) : sc.status === 'not_linked' ? (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Badge tone="amber">{t('admin.contentPlan.setupRequired')}</Badge>
          <span className="text-ink-2">{t('admin.contentPlan.scNotLinked')}</span>
          <Link to="/google" className="text-pine underline underline-offset-2">
            {t('admin.nav.google')}
          </Link>
        </div>
      ) : sc.status === 'no_data' ? (
        <p className="text-sm text-ink-2">{t('admin.contentPlan.scNoData')}</p>
      ) : (
        <>
          <p className="mb-3 text-sm text-ink-2">
            {t('admin.contentPlan.scRange', {
              from: formatDate(sc.range.from, false),
              to: formatDate(sc.range.to, false),
            })}
          </p>
          {!sc.items.length ? (
            <p className="text-sm text-ink-2">{t('admin.contentPlan.scNoClicks')}</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-line">
              <table className="w-full text-sm">
                <thead className="bg-surface-2 text-ink-2">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-start font-medium">
                      {t('admin.contentPlan.post')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-end font-medium">
                      {t('admin.sc.metrics.clicks')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-end font-medium">
                      {t('admin.sc.metrics.impressions')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-end font-medium">
                      {t('admin.sc.metrics.ctr')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-end font-medium">
                      {t('admin.sc.metrics.position')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sc.items.map((p) => (
                    <tr key={p.id} className="border-t border-line">
                      <td className="px-3 py-2">
                        <Link to="/blog/$id" params={{ id: p.id }} className="text-pine hover:underline">
                          {p.title}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-end tabular-nums">{formatNumber(p.clicks)}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{formatNumber(p.impressions)}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{pct.format(p.ctr)}</td>
                      <td className="px-3 py-2 text-end tabular-nums">
                        {p.position === null ? '—' : dec.format(p.position)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-2 text-sm text-ink-3">
            {t('admin.contentPlan.scWithoutClicks', { count: sc.withoutClicks })}
          </p>
        </>
      )}
    </Section>
  );
}
